import { z } from 'zod';

// User registration schema
export const registerSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  fullName: z.string().min(2, 'Full name must be at least 2 characters'),
  company: z.string().optional(),
});

// Login schema
export const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

// CV upload schema
export const cvUploadSchema = z.object({
  name: z.string().min(1, 'Candidate name is required'),
  cv: z.instanceof(File).refine(file => file.size > 0, 'Please select a CV file'),
});

// Candidate schema for upload forms
export const candidateSchema = z.object({
  candidateName: z.string().min(1, 'Candidate name is required'),
  cvText: z.string().min(1, 'CV text is required'),
});

// Job schema for upload forms
export const jobSchema = z.object({
  jobTitle: z.string().min(1, 'Job title is required'),
  jobDescription: z.string().min(1, 'Job description is required'),
});

// Enterprise subscription schema (updated for new licensing model)
export const subscriptionSchema = z.object({
  plan: z.enum(['enterprise']),
  status: z.enum(['active', 'trialing', 'past_due', 'canceled']),
  analysisCount: z.number().min(0),
  licensesUsed: z.number().min(0).max(100).optional(),
  maxLicenses: z.number().min(1).max(100).optional(),
  organizationId: z.string().uuid().optional(),
});

// Organization schema
export const organizationSchema = z.object({
  name: z.string().min(2, 'Organization name must be at least 2 characters').max(100),
  licenses_purchased: z.number().min(1).max(100),
  licenses_used: z.number().min(0).max(100),
});

// License update schema
export const licenseUpdateSchema = z.object({
  organizationId: z.string().uuid('Invalid organization ID'),
  newLicenseCount: z.number().min(1, 'Minimum 1 license required').max(100, 'Maximum 100 licenses allowed'),
});

// Member invite schema
export const memberInviteSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  role: z.enum(['admin', 'member']),
  organizationId: z.string().uuid('Invalid organization ID'),
});

export type RegisterData = z.infer<typeof registerSchema>;
export type LoginData = z.infer<typeof loginSchema>;
export type CVUploadData = z.infer<typeof cvUploadSchema>;
export type CandidateData = z.infer<typeof candidateSchema>;
export type JobData = z.infer<typeof jobSchema>;
export type SubscriptionData = z.infer<typeof subscriptionSchema>;
export type OrganizationData = z.infer<typeof organizationSchema>;
export type LicenseUpdateData = z.infer<typeof licenseUpdateSchema>;
export type MemberInviteData = z.infer<typeof memberInviteSchema>;